import { useState, useEffect } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import { MessageCircle, ThumbsUp, Lightbulb, Plus, Crown } from "lucide-react";

const TopicOfMonth = ({ isAdmin, compact = false }) => {
  const [topic, setTopic] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showSet, setShowSet] = useState(false);
  const [sugForm, setSugForm] = useState({ title: "", description: "" });
  const [setForm, setSetForm] = useState({ title: "", description: "", month: new Date().toISOString().slice(0, 7) });

  useEffect(() => {
    axios.get(`${API}/topic-of-month/current`).then(r => setTopic(r.data && r.data.topic_id ? r.data : null)).catch(() => {});
    axios.get(`${API}/topic-of-month/suggestions`).then(r => setSuggestions(r.data)).catch(() => {});
  }, []);

  const handleSuggest = async (e) => {
    e.preventDefault();
    if (!sugForm.title) { toast.error("Enter a topic title"); return; }
    try {
      await axios.post(`${API}/topic-of-month/suggestions`, sugForm);
      toast.success("Topic suggested!");
      setShowSuggest(false);
      setSugForm({ title: "", description: "" });
      const res = await axios.get(`${API}/topic-of-month/suggestions`);
      setSuggestions(res.data);
    } catch { toast.error("Failed"); }
  };

  const handleVote = async (id) => {
    try {
      await axios.post(`${API}/topic-of-month/suggestions/${id}/vote`);
      const res = await axios.get(`${API}/topic-of-month/suggestions`);
      setSuggestions(res.data);
    } catch { toast.error("Failed"); }
  };

  const handleSetTopic = async (e) => {
    e.preventDefault();
    if (!setForm.title) { toast.error("Enter topic title"); return; }
    try {
      await axios.post(`${API}/topic-of-month`, setForm);
      toast.success("Topic of the month set!");
      setShowSet(false);
      const res = await axios.get(`${API}/topic-of-month/current`);
      setTopic(res.data && res.data.topic_id ? res.data : null);
    } catch { toast.error("Failed"); }
  };

  // Parse "YYYY-MM" in LOCAL time. `new Date("YYYY-MM-01")` parses as UTC
  // midnight, which rolls back to the previous month in Western timezones
  // (the bug user reported: "month label shows wrong month").
  const monthName = topic?.month
    ? (() => {
        const [y, m] = topic.month.split("-").map(Number);
        return new Date(y, (m || 1) - 1, 1).toLocaleDateString("en-CA", { month: "long", year: "numeric" });
      })()
    : "";

  return (
    <div data-testid="topic-of-month">
      {/* Current Topic Banner */}
      {topic ? (
        <div className="bg-gradient-to-r from-[#6B4E8A] to-[#553D6F] rounded-2xl p-5 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/20 flex items-center justify-center shrink-0">
                <Crown className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <div>
                <p className="text-xs text-[#8DB5A0] font-medium uppercase tracking-wider">Topic of the Month — {monthName}</p>
                <h3 className="text-white font-medium text-lg mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{topic.title}</h3>
                {!compact && topic.description && <p className="text-white/60 text-sm mt-1">{topic.description}</p>}
              </div>
            </div>
            <Link to={topic.discussion_id ? `/discussions/${topic.discussion_id}` : `/discussions?search=${encodeURIComponent(topic.title)}`} className="shrink-0">
              <Button size="sm" className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full text-xs" data-testid="discuss-topic-btn">
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> {topic.discussion_id ? "Open thread" : "Discuss"}
              </Button>
            </Link>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-[#FAF7F2] border border-dashed border-[#E5DFEC] rounded-2xl p-5 mb-6 text-center">
          <p className="text-sm text-[#64748B] mb-3">No topic set for this month</p>
          <Dialog open={showSet} onOpenChange={setShowSet}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="set-topic-btn">Set Topic of the Month</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Set Topic of the Month</DialogTitle></DialogHeader>
              <form onSubmit={handleSetTopic} className="space-y-4 mt-4">
                <div><Label>Month</Label><Input type="month" value={setForm.month} onChange={e => setSetForm({...setForm, month: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div><Label>Topic Title *</Label><Input value={setForm.title} onChange={e => setSetForm({...setForm, title: e.target.value})} placeholder="e.g. Work-Life Balance in Dental Hygiene" className="mt-2 bg-white border-[#E5DFEC]" data-testid="topic-title-input" /></div>
                <div><Label>Description</Label><Textarea value={setForm.description} onChange={e => setSetForm({...setForm, description: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div className="text-xs text-[#64748B] bg-[#F0ECF7] rounded-xl p-3 leading-relaxed">
                  <strong className="text-[#6B4E8A]">Heads up:</strong> publishing this will also spawn a <strong>starred discussion thread</strong> on the Discussions page, so members can reply immediately.
                </div>
                <Button type="submit" className="w-full bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full" data-testid="confirm-topic-btn">Set Topic &amp; Open Thread</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      ) : null}

      {/* Suggestion Box + Voting (not on compact view) */}
      {!compact && (
        <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-[#8DB5A0]" />
              <h3 className="font-medium text-[#6B4E8A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Suggest Next Month's Topic</h3>
            </div>
            <Dialog open={showSuggest} onOpenChange={setShowSuggest}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid="suggest-topic-btn"><Plus className="w-3 h-3 mr-1" /> Suggest</Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Suggest a Topic</DialogTitle></DialogHeader>
                <form onSubmit={handleSuggest} className="space-y-4 mt-4">
                  <div><Label>Topic *</Label><Input value={sugForm.title} onChange={e => setSugForm({...sugForm, title: e.target.value})} placeholder="Your topic idea" className="mt-2 bg-white border-[#E5DFEC]" data-testid="suggest-title-input" /></div>
                  <div><Label>Why? (optional)</Label><Textarea value={sugForm.description} onChange={e => setSugForm({...sugForm, description: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <Button type="submit" className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="submit-suggest-btn">Submit</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {suggestions.length > 0 ? (
            <div className="space-y-2">
              {suggestions.slice(0, 5).map(s => (
                <div key={s.suggestion_id} className="flex items-center justify-between p-2.5 bg-[#FAF7F2] rounded-xl">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#6B4E8A] font-medium truncate">{s.title}</p>
                    {s.description && <p className="text-xs text-[#64748B] truncate">{s.description}</p>}
                  </div>
                  <button onClick={() => handleVote(s.suggestion_id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs hover:bg-[#8DB5A0]/10 transition-colors shrink-0 ml-2" data-testid={`vote-${s.suggestion_id}`}>
                    <ThumbsUp className="w-3.5 h-3.5 text-[#8DB5A0]" />
                    <span className="font-medium text-[#6B4E8A]">{s.vote_count}</span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#64748B] text-center py-2">No suggestions yet — be the first!</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TopicOfMonth;
